import { uniqBy } from 'ramda';
import { useState, useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { unzip } from 'unzipit';

import { Offer } from 'modules/shared-menu/pages/SharedMenuEditorPage/types';

import { FilesMatch, MatchIdentifier, UploadError } from '../types';
import { getFileNameId, isImageFileName, matchFilesWithOffers } from '../utils/utils';

const isNotSystemFile = (name: string) =>
  ['DS_Store', '__MACOSX'].every(systemFileName => !name.includes(systemFileName));

const unpackUniqueFiles = async (files: File[]) => {
  const unpackedFiles: File[] = [];
  for (const file of files) {
    if (file.name.endsWith('.zip')) {
      try {
        const { entries } = await unzip(file);
        for (const [name, entry] of Object.entries(entries)) {
          if (isNotSystemFile(name)) {
            const blob = await entry.blob();
            const newFile = new File([blob], name);
            unpackedFiles.push(newFile);
          }
        }
      } catch (error) {
        unpackedFiles.push(file);
      }
    } else if (isNotSystemFile(file.name)) {
      unpackedFiles.push(file);
    }
  }
  return uniqBy(file => file.name, unpackedFiles);
};

const useFileMatcher = (items: Offer[]) => {
  const intl = useIntl();

  const [results, setResults] = useState<FilesMatch[] | null>(null);
  const [isMatching, setIsMatching] = useState(false);
  const [matchBy, setMatchBy] = useState<MatchIdentifier>('id');

  useEffect(() => {
    return () => {
      results?.forEach(result => {
        const previews = Object.values(result.previews || {});
        if (previews.length) {
          previews.forEach(preview => URL.revokeObjectURL(preview));
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setErrors = useCallback(
    (errors: UploadError[]) => {
      const resultsWithErrors = (results || []).map(result => {
        const uploadError = errors.find(error => getFileNameId(result.files[0].name) === error.fileNameId);
        if (result.status === 'success' && uploadError) {
          return {
            ...result,
            status: 'failure' as const,
            note: `${intl.formatMessage({ id: 'menu.bulk-image.failed-upload' })}. ${uploadError.error}`,
          };
        }
        return result;
      });
      setResults(resultsWithErrors);
    },
    [intl, results],
  );

  const handleFileMatching = useCallback(
    async (files: File[]) => {
      setIsMatching(true);
      const uniqueUnpackedFiles = await unpackUniqueFiles(files);
      const matchResults = await matchFilesWithOffers(uniqueUnpackedFiles, items, matchBy, intl);
      const newResults = matchResults
        .map((record: FilesMatch) => ({
          ...record,
          previews: record.files.reduce(
            (previews, file) =>
              isImageFileName(file.name) ? { ...previews, [file.name]: URL.createObjectURL(file) } : previews,
            {},
          ),
        }))
        .sort((record: FilesMatch) => (record.status === 'failure' ? -1 : 1));
      setResults(newResults);
      setIsMatching(false);
    },
    [items, intl, matchBy],
  );

  const resetMatcherState = () => {
    setResults(null);
    setMatchBy('id');
  };

  return { handleFileMatching, results, matchBy, setMatchBy, isMatching, setErrors, resetMatcherState };
};

export default useFileMatcher;
